import type { Temperature } from 'app/shared/types/Temperature';

import type { RentCount } from 'app/shared/types/RentCount';
import type { HistoricRentPrice } from 'app/shared/types/HistoricRentPrice';
import type { MarketTrendsApiType } from 'app/shared/types/ApiResponseTypes';
import type { AreaType } from 'app/shared/types/AreaType';
import type { Market } from 'app/shared/types/Market';
import pathsManifest from 'app/shared/utils/pathsManifest';

class MarketTrends {
  date: string | null;
  areaName: string | null;
  areaType: AreaType | null;
  medianRent: number | null;
  minRent: number | null;
  maxRent: number | null;
  monthlyChange: number | null;
  yearlyChange: number | null;
  availableRentals: number | null;
  avgDaysOnMarket: number | null;
  temperature: Temperature | null;
  rentCounts: Array<RentCount>;
  curYearRentPrices: Array<HistoricRentPrice>;
  prevYearRentPrices: Array<HistoricRentPrice>;
  medianNationalRent: number | null;
  zillowRentalsSrpUrl: string | null;
  resourceId: string;
  rentCompare: { medianRent: number; otherResourceId: string; areaName?: string };
  nearbyAreas: Array<{ name: string; resourceId: string }>;
  zipcodesInCity: Array<{ name: string; resourceId: string }>;
  nearbyAreaTrends: Array<Market>;

  constructor(res: MarketTrendsApiType = <MarketTrendsApiType>{}, resourceId: string = '', otherResourceId = '') {
    this.date = res?.date ?? null;
    this.areaName = res?.areaName ?? null;
    this.medianRent = res?.summary?.medianRent ?? null;
    this.monthlyChange = res?.summary?.monthlyChange ?? null;
    this.yearlyChange = res?.summary?.yearlyChange ?? null;
    this.availableRentals = res?.summary?.availableRentals ?? null;
    this.avgDaysOnMarket = res?.summary?.avgDaysOnMarket ?? null;
    this.temperature = res?.marketTemperature?.temperature ?? null;
    this.medianNationalRent = res?.rentCompare?.medianRent ?? null;
    this.zillowRentalsSrpUrl = res?.zillowRentalsSrpUrl ?? null;
    this.minRent = res?.rentHistogram?.minPrice ?? null;
    this.maxRent = res?.rentHistogram?.maxPrice ?? null;
    this.rentCompare =
      res && res.rentCompare
        ? {
            areaName: res.rentCompare.areaName,
            medianRent: res.rentCompare.medianRent,
            otherResourceId,
          }
        : {
            medianRent: 0,
            otherResourceId,
          };
    this.rentCounts = res?.rentHistogram?.priceAndCount ?? [];
    this.curYearRentPrices = res?.medianRentPriceOverTime?.currentYear ?? [];
    this.prevYearRentPrices = res?.medianRentPriceOverTime?.prevYear ?? [];
    this.resourceId = resourceId;
    this.nearbyAreas =
      res?.nearByAreas?.map(({ name, resourceName }) => {
        return { name, resourceId: resourceName };
      }) ?? [];
    this.zipcodesInCity =
      res?.zipcodesInCity?.map(({ name, resourceName }) => {
        return { name, resourceId: resourceName };
      }) ?? [];
    this.areaType = res?.areaType ?? null;
    this.nearbyAreaTrends =
      res?.nearbyAreaTrends?.map((market) => {
        return {
          url: `${pathsManifest.MarketTrendsPage}/${market.resourceId}/`,
          resourceId: market.resourceId,
          name: market.areaName,
          medianRent: market.medianRent,
          prices: market.medianRentPriceOverTime.map(({ price }) => price),
        };
      }) ?? [];
  }
}

export default MarketTrends;
